import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DeepCopyPipe, MomentPipe } from '@quorum/common-pipes';
import {
  ServiceAppointment as Appointment,
  AppointmentCase,
  QuoteDetail,
  ServiceCode,
  ServiceGroup,
  WorkOrder,
  WorkorderDeclinedMpviCode,
  Employee,
  Note,
  DeclinedReason,
} from '@quorum/models/xs-resource';
import {
  appointmentCaseAdapter,
  quoteLinesAdapter,
  serviceAppointmentDeclinedOperationsAdapter,
  serviceGroupsAdapter,
} from '@quorum/sha-entities/fixed-operations';
import { selectAuthenticationEmployee } from 'libs/authentication/src/lib/+state/authentication.interfaces';
import { serviceLaneDropdownsQuery } from 'libs/service-lane-dropdowns/src/lib/+state/service-lane-dropdowns.selectors';
import {
  appointmentAdapter,
  ServiceLaneState,
  SERVICELANE_FEATURE_KEY,
  workOrderAppointmentAdapter,
  alternateTransportationsAdapter,
} from './service-lane.reducer';

// Lookup the 'ServiceLane' feature state managed by NgRx
const getServiceLaneState = createFeatureSelector<ServiceLaneState>(SERVICELANE_FEATURE_KEY);

const getVehicle = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.vehicle);

const getCustomerAssociate = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.customerAssociate);

const getCustomerAddress = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.customerAddress);

const getCustomerNotes = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.customerNotes);
const getAppointmentNotes = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.appointmentNotes);
const getVehicleNotes = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.vehicleNotes);

const getAllNotes = createSelector(
  getCustomerNotes,
  getAppointmentNotes,
  getVehicleNotes,
  (customerNotes: Note, appointmentNotes: Note, vehicleNotes: Note) => {
    return { customerNotes, appointmentNotes, vehicleNotes };
  }
);

const openAppointments = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.openAppointments);

const selectedAppointmentCases = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.selectedAppointmentCases
);

const todaysAppointments = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.todaysAppointments);

const todaysAppointmentsLoading = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.todaysAppointments.loading
);

const todaysWorkOrderAppointments = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.todaysWorkOrderAppointments
);

const openAppointmentStatus = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.openAppointments.status
);

const unacknowledgedAppointments = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.unacknowledgedAppointments
);

const unacknowledgedAppointmentsStatus = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.unacknowledgedAppointments.status
);

const selectedAppointmentCasesStatus = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.selectedAppointmentCases.status
);

const selectedAppointmentCasesError = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.selectedAppointmentCases.error
);

const serviceHistory = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.serviceHistory);

const serviceGroups = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.serviceGroups);

const quoteLines = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.quoteLines);

const approvedQuoteLines = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.approvedQuoteLines);

const declinedQuoteLines = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.declinedQuoteLines);

const serviceAppointmentDeclinedOperations = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.serviceAppointmentDeclinedOperations
);
const getSelectedAppointment = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.selectedAppointment
);

const getUi = createSelector(getServiceLaneState, (state: ServiceLaneState) => state.ui);

const getAppointmentFilters = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.appointmentFilters
);

const alternateTransportations = createSelector(
  getServiceLaneState,
  (state: ServiceLaneState) => state.alternateTransportations
);

const alternateTransportationsSelectors = alternateTransportationsAdapter.getSelectors(alternateTransportations);
const openAppointmentsSelectors = appointmentAdapter.getSelectors(openAppointments);
const unacknowledgedAppointmentsSelector = appointmentAdapter.getSelectors(unacknowledgedAppointments);
const todaysAppointmentsSelectors = appointmentAdapter.getSelectors(todaysAppointments);
const todaysWorkOrderAppointmentsSelectors = workOrderAppointmentAdapter.getSelectors(todaysWorkOrderAppointments);
const serviceHistorySelectors = workOrderAppointmentAdapter.getSelectors(serviceHistory);
const serviceGroupsSelectors = serviceGroupsAdapter.getSelectors(serviceGroups);
const quoteLinesSelectors = quoteLinesAdapter.getSelectors(quoteLines);
const approvedQuoteLinesSelectors = quoteLinesAdapter.getSelectors(approvedQuoteLines);
const declinedQuoteLinesSelectors = quoteLinesAdapter.getSelectors(declinedQuoteLines);
const serviceAppointmentDeclinedOperationsSelectors = serviceAppointmentDeclinedOperationsAdapter.getSelectors(
  serviceAppointmentDeclinedOperations
);
const selectedAppointmentCaseSelectors = appointmentCaseAdapter.getSelectors(selectedAppointmentCases);
export const getMyAppointments = createSelector(
  todaysAppointmentsSelectors.selectAll,
  todaysWorkOrderAppointmentsSelectors.selectAll,
  serviceLaneDropdownsQuery.serviceCodes,
  serviceLaneDropdownsQuery.getVehicleDropdowns,
  selectAuthenticationEmployee,
  (
    appointments: Appointment[],
    workOrders: WorkOrder[],
    serviceCodes: ServiceCode[],
    vehicleDropdowns,
    employee: Employee
  ) => {
    if (employee != null) {
      const myApts = appointments;
      const myWos = workOrders;
      const myAppointments: any[] = [];
      myApts.forEach((apt) => {
        myAppointments.push({
          appointment: apt,
          ridePreference: apt.embedded.transportOption ? apt.embedded.transportOption.name : null,
          vendorName: apt.vendorName,
          customer: apt.embedded.customerAssociate,
          customerName: apt.customerName,
          type: 'appointment',
          time: apt.dateTime,
          date: new MomentPipe().transform(apt.dateTime, 'YYYYMMDD'),
          vehicle: apt.embedded.vehicle,
          vehicleDescription: apt.vehicleDescription,
          id: apt.id,
          phoneNumber: apt.phoneNumber,
          serviceAdvisor: apt.embedded.serviceAdvisorAssociate,
          serviceCode: serviceCodes.find((serviceCode) => serviceCode.serviceCode === apt.serviceCode)
            ? serviceCodes.find((serviceCode) => serviceCode.serviceCode === apt.serviceCode).description
            : 'No service code indicated.',
          digitalCheckInStatus: apt.digitalCheckInStatus,
          vehicleColour: vehicleDropdowns.colours.find(
            (vehicleColour) => vehicleColour.id === apt.embedded?.vehicle?.prospectColourId
          ),
        });
      });

      myWos.forEach((wo) => {
        myAppointments.push({
          workOrder: wo,
          customer: wo.embedded.customerAssociate,
          type: 'workOrder',
          date: new MomentPipe().transform(wo.appointmentDateTime, 'YYYYMMDD'),
          time: wo.appointmentDateTime,
          vehicle: wo.embedded.vehicle,
          id: wo.id,
          serviceAdvisor: wo.embedded.serviceAdvisorAssociate,
          serviceCode: serviceCodes.find((serviceCode) => serviceCode.serviceCode === wo.serviceCode)
            ? serviceCodes.find((serviceCode) => serviceCode.serviceCode === wo.serviceCode).description
            : 'No service code indicated.',
          digitalCheckInStatus: null,
          vehicleColour: vehicleDropdowns.colours.find(
            (vehicleColour) => vehicleColour.id === wo.embedded?.vehicle?.prospectColourId
          ),
        });
      });
      return myAppointments;
    }
    return [];
  }
);

export const getUnacknowledgedAppointmentCards = createSelector(
  unacknowledgedAppointmentsSelector.selectAll,
  serviceLaneDropdownsQuery.serviceCodes,
  serviceLaneDropdownsQuery.getVehicleDropdowns,
  selectAuthenticationEmployee,
  (unacknowledgedAppointments: Appointment[], serviceCodes: ServiceCode[], vehicleDropdowns, employee: Employee) => {
    if (employee != null) {
      const unacknowledgedAppointmentsCards: any[] = [];
      unacknowledgedAppointments.forEach((apt) => {
        unacknowledgedAppointmentsCards.push({
          appointment: apt,
          ridePreference: apt.embedded.transportOption ? apt.embedded.transportOption.name : null,
          vendorName: apt.vendorName,
          customer: apt.embedded.customerAssociate,
          customerName: apt.customerName,
          type: 'appointment',
          time: apt.dateTime,
          date: new MomentPipe().transform(apt.dateTime, 'YYYYMMDD'),
          vehicle: apt.embedded.vehicle,
          vehicleDescription: apt.vehicleDescription,
          id: apt.id,
          phoneNumber: apt.phoneNumber,
          serviceAdvisor: apt.embedded.serviceAdvisorAssociate,
          serviceCode: serviceCodes.find((serviceCode) => serviceCode.serviceCode === apt.serviceCode)
            ? serviceCodes.find((serviceCode) => serviceCode.serviceCode === apt.serviceCode).description
            : 'No service code indicated.',
          digitalCheckInStatus: apt.digitalCheckInStatus,
          vehicleColour: vehicleDropdowns.colours.find(
            (vehicleColour) => vehicleColour.id === apt.embedded?.vehicle?.prospectColourId
          ),
        });
      });
      return unacknowledgedAppointmentsCards;
    }
    return [];
  }
);

export const getQuoteLines = createSelector(quoteLinesSelectors.selectAll, (quoteLines: QuoteDetail[]) => {
  return quoteLines.filter((line) => {
    return new Date(line.expirationDate) > new Date() && line.sourceType == null && line.declined == null;
  });
});

export const getAllNonExpiredQuoteLines = createSelector(quoteLinesSelectors.selectAll, (quoteLines: QuoteDetail[]) => {
  return quoteLines.filter((line) => {
    return new Date(line.expirationDate) > new Date() && line.sourceType === null;
  });
});

export const quoteLinesGeneratedFromCurrentTransaction = (appointmentId: number, workOrderId: number) =>
  createSelector(getQuoteLines, (quoteLines: QuoteDetail[]) => {
    return quoteLines.filter(
      (ql) =>
        (ql.createdFromSourceType == 'SA' && ql.createdFromSourceReference == appointmentId) ||
        (ql.createdFromSourceType == 'WO' && ql.createdFromSourceReference == workOrderId)
    );
  });

export const getApprovedQuoteLines = createSelector(
  approvedQuoteLinesSelectors.selectAll,
  (quoteLines: QuoteDetail[]) => {
    return quoteLines.filter((ql) => {
      return ql.embedded != null;
    });
  }
);

export const getApprovedQuoteLinesWithServiceGroup = createSelector(
  approvedQuoteLinesSelectors.selectAll,
  serviceGroupsSelectors.selectAll,
  (approvedQuoteLines: QuoteDetail[], serviceGroups: ServiceGroup[]): QuoteDetail[] => {
    return approvedQuoteLines.map((aql) => {
      let sg = serviceGroups.find((serviceGroup) => serviceGroup.id === aql.serviceGroupId);
      sg && aql.embedded ? (aql.embedded.serviceGroup = sg) : null;
      return aql;
    });
  }
);

export const quoteLineWithServiceGroupExists = createSelector(
  getQuoteLines,
  (quoteLines: QuoteDetail[], props: any) => {
    return quoteLines.findIndex(
      (ql) =>
        ql.serviceGroupId == props.serviceGroupId &&
        ql.createdFromSourceType == 'SA' &&
        ql.createdFromSourceReference == props.appointmentId
    ) > -1
      ? true
      : false;
  }
);

/**
 * Created for GMMP project because GM service do not have a service group so we have to use opCode
 */
export const quoteLineWithOpCodeExists = (opCode: string, appointmentId: number) =>
  createSelector(getAllNonExpiredQuoteLines, (quoteLines: QuoteDetail[]) => {
    return quoteLines.findIndex(
      (ql) =>
        ql.opCode === opCode && ql.createdFromSourceType === 'SA' && ql.createdFromSourceReference === appointmentId
    ) > -1
      ? true
      : false;
  });

export const activeQuoteLineWithServiceGroupExists = (serviceGroupId: string) =>
  createSelector(
    getAllNonExpiredQuoteLines,
    serviceLaneDropdownsQuery.declinedReasons,
    (quoteLines: QuoteDetail[], declinedReasons: DeclinedReason[]) => {
      const filteredQuoteLines = quoteLines.filter((ql) => ql.serviceGroupId === serviceGroupId);
      const moveToQuoteHistoryDeclinedReasons = declinedReasons.filter(
        (declinedReason) => declinedReason.moveToQuoteHistoryFlag === true
      );
      const quoteLineFound = filteredQuoteLines.find(
        (ql) =>
          !moveToQuoteHistoryDeclinedReasons
            .map((moveToQuoteHistoryDeclinedReasons) => moveToQuoteHistoryDeclinedReasons.id)
            .includes(ql.declined)
      );
      return quoteLineFound ? quoteLineFound : null;
    }
  );

export const getDeclinedQuoteLines = createSelector(
  declinedQuoteLinesSelectors.selectAll,
  (quoteLines: QuoteDetail[]) => {
    return quoteLines.filter((ql) => {
      return ql.embedded != null;
    });
  }
);

export const getMenuQuoteLines = createSelector(quoteLinesSelectors.selectAll, (quoteLines: QuoteDetail[]) => {
  return quoteLines.filter((line) => {
    return (
      new Date(line.expirationDate) > new Date() &&
      line.lineAddedFromServiceSchedule == 1 &&
      line.sourceType == null &&
      line.embedded != null
    );
  });
});

export const getSelectedAppointmentCasesWithServiceGroupAndRate = createSelector(
  selectedAppointmentCaseSelectors.selectAll,
  serviceGroupsSelectors.selectAll,
  (
    apptCases: AppointmentCase[],
    serviceGroups: ServiceGroup[]
  ): { apptCase: AppointmentCase; serviceGroup: ServiceGroup }[] => {
    return apptCases.map((apptCase) => {
      let sg = serviceGroups.find((serviceGroup) => serviceGroup.id === apptCase.serviceGroupId);

      return { apptCase: apptCase, serviceGroup: sg };
    });
  }
);

export const appointmentCaseWithOpCodeExists = (opCode: string, appointmentId: number) =>
  createSelector(selectedAppointmentCaseSelectors.selectAll, (appointmentCases: AppointmentCase[]) => {
    return appointmentCases.findIndex(
      (appointmentCase) => appointmentCase.opCode == opCode && appointmentCase.appointmentId == appointmentId
    ) > -1
      ? true
      : false;
  });

export const getMenuQuoteLinesWithStatuses = createSelector(
  getMenuQuoteLines,
  getApprovedQuoteLines,
  getDeclinedQuoteLines,
  serviceGroupsSelectors.selectAll,
  (
    quoteLines: QuoteDetail[],
    approvedQuoteLines: QuoteDetail[],
    declinedQuoteLines: QuoteDetail[],
    serviceGroups: ServiceGroup[]
  ): { status: string; quoteLine: QuoteDetail; serviceGroup: ServiceGroup }[] => {
    return quoteLines.map((quoteLine) => {
      let status = null;
      let ql: QuoteDetail;
      const foundDeclinedReason = declinedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      const foundApprovedReason = approvedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      const sg = serviceGroups.find((serviceGroup) => serviceGroup.id === quoteLine.serviceGroupId);

      if (foundDeclinedReason) {
        ql = foundDeclinedReason;
        status = 'declined';
      } else if (foundApprovedReason) {
        ql = foundApprovedReason;
        status = 'approved';
      }
      return { status: status, quoteLine: ql ? ql : quoteLine, serviceGroup: sg };
    });
  }
);

export const getCampaignQuoteLines = createSelector(quoteLinesSelectors.selectAll, (quoteLines: QuoteDetail[]) => {
  return quoteLines.filter((line) => {
    return (
      new Date(line.expirationDate) > new Date() &&
      line.declined == null &&
      line.sourceType == null &&
      line.embedded != null &&
      line.campaignNumber != null
    );
  });
});

export const getCampaignQuoteLinesWithStatuses = createSelector(
  getCampaignQuoteLines,
  getApprovedQuoteLines,
  getDeclinedQuoteLines,
  serviceGroupsSelectors.selectAll,
  (
    quoteLines: QuoteDetail[],
    approvedQuoteLines: QuoteDetail[],
    declinedQuoteLines: QuoteDetail[],
    serviceGroups: ServiceGroup[]
  ): { status: string; quoteLine: QuoteDetail }[] => {
    return quoteLines.map((quoteLine) => {
      let status = null;
      let ql: QuoteDetail;
      let foundDeclinedReason = declinedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      let foundApprovedReason = approvedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      let sg = serviceGroups.find((serviceGroup) => serviceGroup.id === quoteLine.serviceGroupId);

      if (foundDeclinedReason) {
        ql = foundDeclinedReason;
        status = 'declined';
      } else if (foundApprovedReason) {
        ql = foundApprovedReason;
        status = 'approved';
      }
      return { status: status, quoteLine: ql ? ql : quoteLine, serviceGroup: sg };
    });
  }
);

export const getMPVIQuoteLines = createSelector(quoteLinesSelectors.selectAll, (quoteLines: QuoteDetail[]) => {
  return quoteLines.filter((line) => {
    return (
      new Date(line.expirationDate) > new Date() &&
      line.insertedFromVehicleInspFlag == 1 &&
      line.declined == null &&
      line.sourceType == null &&
      line.embedded != null
    );
  });
});

export const getWorkOrderMPVIQuoteLines = createSelector(
  quoteLinesSelectors.selectAll,
  (quoteLines: QuoteDetail[], props: { workOrderId: number }) => {
    return quoteLines.filter((line) => {
      return (
        new Date(line.expirationDate) > new Date() &&
        line.insertedFromVehicleInspFlag == 1 &&
        line.declined == null &&
        line.sourceType == null &&
        line.embedded != null &&
        line.workHeaderId === props.workOrderId
      );
    });
  }
);

export const getMPVIQuoteLinesWithStatuses = createSelector(
  getMPVIQuoteLines,
  getApprovedQuoteLines,
  getDeclinedQuoteLines,
  serviceGroupsSelectors.selectAll,
  (
    quoteLines: QuoteDetail[],
    approvedQuoteLines: QuoteDetail[],
    declinedQuoteLines: QuoteDetail[],
    serviceGroups: ServiceGroup[]
  ): { status: string; quoteLine: QuoteDetail }[] => {
    return quoteLines.map((quoteLine) => {
      let status = null;
      let ql: QuoteDetail;
      let foundDeclinedReason = declinedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      let foundApprovedReason = approvedQuoteLines.find((declinedQuoteLine) => quoteLine.id === declinedQuoteLine.id);
      let sg = serviceGroups.find((serviceGroup) => serviceGroup.id === quoteLine.serviceGroupId);

      if (foundDeclinedReason) {
        ql = new DeepCopyPipe().transform(foundDeclinedReason);
        status = 'declined';
      } else if (foundApprovedReason) {
        ql = new DeepCopyPipe().transform(foundApprovedReason);
        status = 'approved';
      }
      return { status: status, quoteLine: ql ? ql : quoteLine, serviceGroup: sg };
    });
  }
);

// export const hasRight = (rightCode: RightCode) => createSelector(selectUser, (user) => {
//   return user?.rights?.indexOf(rightCode) !== -1;
// });

export const getQuoteLineActionedPercentages = (props: any) =>
  createSelector(
    getMPVIQuoteLines,
    getMenuQuoteLines,
    getCampaignQuoteLines,
    getApprovedQuoteLines,
    getDeclinedQuoteLines,
    (
      mpviQuoteLines: QuoteDetail[],
      menuQuoteLines: QuoteDetail[],
      campaignQuoteLines: QuoteDetail[],
      approvedQuoteLines: QuoteDetail[],
      declinedQuoteLines: QuoteDetail[]
    ): { mpviPercentage: number; menuPercentage: number; campaignPercentage: number } => {
      let mpviTotal: number = mpviQuoteLines.filter(
        (ql) =>
          ql.createdFromSourceReference == props.appointmentId || ql.createdFromSourceReference == props.workOrderId
      ).length;

      let menuTotal: number = menuQuoteLines.filter(
        (ql) => ql.createdFromSourceReference == props.appointmentId
      ).length;
      let campaignTotal: number = campaignQuoteLines.filter(
        (ql) => ql.createdFromSourceReference == props.appointmentId
      ).length;

      let mpviActioned: number = mpviQuoteLines.filter((ql) => {
        return (
          approvedQuoteLines.findIndex(
            (q) =>
              q.id == ql.id &&
              (ql.createdFromSourceReference == props.appointmentId ||
                ql.createdFromSourceReference == props.workOrderId)
          ) > -1 ||
          declinedQuoteLines.findIndex(
            (q) =>
              q.id == ql.id &&
              (ql.createdFromSourceReference == props.appointmentId ||
                ql.createdFromSourceReference == props.workOrderId)
          ) > -1
        );
      }).length;

      let menuActioned: number = menuQuoteLines.filter((ql) => {
        return (
          approvedQuoteLines.findIndex((q) => q.id == ql.id && ql.createdFromSourceReference == props.appointmentId) >
            -1 ||
          declinedQuoteLines.findIndex((q) => q.id == ql.id && ql.createdFromSourceReference == props.appointmentId) >
            -1
        );
      }).length;

      let campaignActioned: number = campaignQuoteLines.filter((ql) => {
        return (
          approvedQuoteLines.findIndex((q) => q.id == ql.id && ql.createdFromSourceReference == props.appointmentId) >
            -1 ||
          declinedQuoteLines.findIndex((q) => q.id == ql.id && ql.createdFromSourceReference == props.appointmentId) >
            -1
        );
      }).length;

      let menuPercentage = menuTotal > 0 ? Math.round((menuActioned / menuTotal) * 100) : 0;
      let mpviPercentage = mpviTotal > 0 ? Math.round((mpviActioned / mpviTotal) * 100) : 0;
      let campaignPercentage = campaignTotal > 0 ? Math.round((campaignActioned / campaignTotal) * 100) : 0;
      return { mpviPercentage: mpviPercentage, menuPercentage: menuPercentage, campaignPercentage: campaignPercentage };
    }
  );

export const getWorkOrdersWithDeclinedServicesForVehicle = createSelector(
  serviceHistorySelectors.selectAll,
  (workOrders: WorkOrder[]) => {
    return workOrders.filter((wo) => {
      return wo.embedded != null && wo.embedded.declinedMpviCodes.length > 0;
    });
  }
);

export const getAllDeclinedServicesForVehicle = createSelector(
  getWorkOrdersWithDeclinedServicesForVehicle,
  (workOrders: WorkOrder[]) => {
    let declinedServices: WorkorderDeclinedMpviCode[] = [];
    workOrders.forEach((wo) => {
      let woDeclinedServices: WorkorderDeclinedMpviCode[] = wo.embedded.declinedMpviCodes;
      woDeclinedServices.forEach((declined) => {
        declinedServices.push(declined);
      });
    });
    return declinedServices;
  }
);

export const serviceLaneQuery = {
  getVehicle,
  getCustomerAssociate,
  getCustomerAddress,
  getAllNotes,
  getMyAppointments,
  getUnacknowledgedAppointmentCards,
  selectOpenAppointments: openAppointmentsSelectors.selectAll,
  getTodaysAppointments: todaysAppointmentsSelectors.selectAll,
  selectUnacknowledgedAppointments: unacknowledgedAppointmentsSelector.selectAll,
  getTodaysWorkOrderAppointments: todaysWorkOrderAppointmentsSelectors,
  getSelectedAppointment,
  getSeletedAppointmentCases: selectedAppointmentCaseSelectors.selectAll,
  getSelectedAppointmentCasesWithServiceGroupAndRate,
  getServiceHistory: serviceHistorySelectors.selectAll,
  getServiceGroups: serviceGroupsSelectors.selectAll,
  getQuoteLines,
  getAllNonExpiredQuoteLines,
  getDeclinedQuoteLines,
  getApprovedQuoteLines,
  getApprovedQuoteLinesWithServiceGroup,
  getMenuQuoteLines,
  getMenuQuoteLinesWithStatuses,
  getMPVIQuoteLines,
  getMPVIQuoteLinesWithStatuses,
  getWorkOrderMPVIQuoteLines,
  getServiceAppointmentDeclinedOperations: serviceAppointmentDeclinedOperationsSelectors.selectAll,
  getUi,
  getAppointmentFilters,
  getTodaysAppointmentsLoading: todaysAppointmentsLoading,
  getOpenAppointmentStatus: openAppointmentStatus,
  getUnacknowledgedAppointmentStatus: unacknowledgedAppointmentsStatus,
  getSelectedAppointmentCasesStatus: selectedAppointmentCasesStatus,
  getSelectedAppointmentCasesError: selectedAppointmentCasesError,
  quoteLineWithServiceGroupExists,
  quoteLineWithOpCodeExists,
  getQuoteLineActionedPercentages,
  getCampaignQuoteLinesWithStatuses,
  getAllDeclinedServicesForVehicle,
  quoteLinesGeneratedFromCurrentTransaction,
  appointmentCaseWithOpCodeExists,
  activeQuoteLineWithServiceGroupExists,
  getAlternateTransportations: alternateTransportationsSelectors.selectAll,
};
